/**
 * Mobile Gallery Enhancements
 *
 * Enhances the product gallery experience on mobile devices with:
 * - Improved swipe gestures with momentum
 * - Pinch-to-zoom support
 * - Better touch feedback
 * - Optimized performance
 */

class MobileGalleryEnhancements extends HTMLElement {
  constructor() {
    super();
    this.gallery = null;
    this.slideshow = null;
    this.slides = [];
    this.currentIndex = 0;

    // Touch tracking
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.isSwiping = false;
    this.swipeThreshold = 50; // pixels
    this.velocityThreshold = 0.3; // pixels per ms

    // Pinch zoom tracking
    this.initialPinchDistance = 0;
    this.currentScale = 1;
    this.isPinching = false;
  }

  connectedCallback() {
    // Only run on mobile devices
    if (window.innerWidth > 749) return;

    this.gallery = this.closest('media-gallery');
    if (!this.gallery) return;

    this.slideshow = this.gallery.querySelector('slideshow-component');
    if (!this.slideshow) return;

    this.slides = Array.from(this.slideshow.querySelectorAll('[ref="slide[]"]'));

    this.init();
  }

  init() {
    // Enhanced touch events for better swipe
    this.slideshow.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.slideshow.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    this.slideshow.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });

    // Add visual feedback
    this.addTouchFeedback();

    // Optimize scroll performance
    this.optimizeScrolling();
  }

  handleTouchStart(event) {
    // Handle pinch zoom (two fingers)
    if (event.touches.length === 2) {
      this.isPinching = true;
      this.initialPinchDistance = this.getPinchDistance(event.touches);
      return;
    }

    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.isSwiping = false;

    // Add active state
    this.slideshow.style.cursor = 'grabbing';
  }

  handleTouchMove(event) {
    // Handle pinch zoom
    if (event.touches.length === 2 && this.isPinching) {
      event.preventDefault();
      const currentDistance = this.getPinchDistance(event.touches);
      const scale = currentDistance / this.initialPinchDistance;
      this.handlePinchZoom(scale);
      return;
    }

    if (event.touches.length !== 1 || this.isPinching) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    // Determine if this is a horizontal swipe
    if (!this.isSwiping && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.isSwiping = true;
      event.preventDefault();
    }

    // Provide visual feedback during swipe
    if (this.isSwiping && Math.abs(deltaX) > 10) {
      const currentSlide = this.slides[this.currentIndex];
      if (currentSlide) {
        const translateAmount = deltaX * 0.3; // Resistance effect
        currentSlide.style.transform = `translateX(${translateAmount}px)`;
      }
    }
  }

  handleTouchEnd(event) {
    // Reset pinch zoom
    if (this.isPinching) {
      this.isPinching = false;
      return;
    }

    if (event.changedTouches.length !== 1) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    const deltaTime = Date.now() - this.touchStartTime;
    const velocity = Math.abs(deltaX) / deltaTime;

    // Reset visual state
    this.slideshow.style.cursor = '';
    const currentSlide = this.slides[this.currentIndex];
    if (currentSlide) {
      currentSlide.style.transform = '';
    }

    // Check if swipe was intentional (based on distance or velocity)
    if (this.isSwiping && (Math.abs(deltaX) > this.swipeThreshold || velocity > this.velocityThreshold)) {
      if (deltaX > 0 && this.currentIndex > 0) {
        // Swipe right - go to previous
        this.navigateToSlide(this.currentIndex - 1);
      } else if (deltaX < 0 && this.currentIndex < this.slides.length - 1) {
        // Swipe left - go to next
        this.navigateToSlide(this.currentIndex + 1);
      }
    }

    this.isSwiping = false;
  }

  getPinchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  handlePinchZoom(scale) {
    // Limit zoom scale
    const newScale = Math.min(Math.max(scale, 1), 3);
    this.currentScale = newScale;

    const currentSlide = this.slides[this.currentIndex];
    if (currentSlide) {
      const img = currentSlide.querySelector('img');
      if (img) {
        img.style.transform = `scale(${newScale})`;
        img.style.transition = 'none';

        // If zoomed out fully, reset
        if (newScale <= 1.05) {
          setTimeout(() => {
            img.style.transform = '';
            img.style.transition = '';
          }, 100);
        }
      }
    }
  }

  navigateToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    this.currentIndex = index;

    // Trigger native slideshow navigation if available
    if (this.gallery && this.gallery.slideshow) {
      this.gallery.slideshow.select(index);
    } else {
      // Fallback: scroll to slide
      const slide = this.slides[index];
      if (slide) {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }

  addTouchFeedback() {
    // Add subtle visual feedback for touch interactions
    this.slides.forEach((slide) => {
      slide.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    });

    // Add haptic feedback on swipe (if supported)
    if ('vibrate' in navigator) {
      this.slideshow.addEventListener('touchend', (event) => {
        if (this.isSwiping && Math.abs(event.changedTouches[0].clientX - this.touchStartX) > this.swipeThreshold) {
          navigator.vibrate(10); // Subtle haptic feedback
        }
      });
    }
  }

  optimizeScrolling() {
    // Use CSS scroll-snap for smoother scrolling
    if (this.slideshow) {
      this.slideshow.style.scrollSnapType = 'x mandatory';
      this.slides.forEach((slide) => {
        slide.style.scrollSnapAlign = 'center';
      });
    }
  }
}

if (!customElements.get('mobile-gallery-enhancements')) {
  customElements.define('mobile-gallery-enhancements', MobileGalleryEnhancements);
}
